import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  FormGroup,
  FormControlLabel,
  Chip,
  Stack,
  IconButton,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import FilterModal from '../../v2/MUI_custom_component/Modal/FilterModal';
import { CustomButton, CustomCheckbox, TextField, CustomRadio, CustomRadioGroup } from '../../v2/MUI_custom_component';
import CustomChip from '../Components/CustomChip';
import { SearchOutlined } from '../../components/icons';
import IconReusable from '../../components/IconReusable';


const DynamicFilter = ({
  open = false,
  onClose,
  title = 'Filter',
  
  filterConfig = [],
  initialFilters = {},
  
  onApply,
  onClear,
}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [selectedCategory, setSelectedCategory] = useState(filterConfig.length > 0 ? filterConfig[0]?.key : null);
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  useEffect(() => {
    // Update selectedCategory when filterConfig changes
    if (filterConfig.length > 0 && !selectedCategory) {
      setSelectedCategory(filterConfig[0].key);
    }
  }, [filterConfig, selectedCategory]);

  useEffect(() => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        count += value.length;
      } else if (typeof value === 'string' && value.trim() !== '') {
        count += 1;
      }
    });
    setActiveFilterCount(count);
  }, [filters]);

  const handleRadioChange = (filterKey, optionKey) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: optionKey,
    }));
  };

  const handleCheckboxChange = (filterKey, optionKey) => {
    setFilters(prev => {
      const current = prev[filterKey] || [];
      return {
        ...prev,
        [filterKey]: current.includes(optionKey)
          ? current.filter(k => k !== optionKey)
          : [...current, optionKey],
      };
    });
  };

  const handleTextChange = (filterKey, value) => {
    // Check if this is a phone number field and apply validation
    const currentFilter = filterConfig.find(f => f.key === filterKey);
    if (currentFilter?.key === 'search_text' || currentFilter?.label?.toLowerCase().includes('phone')) {
      // Only allow digits and limit to 10 characters
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        setFilters(prev => ({
          ...prev,
          [filterKey]: numericValue,
        }));
      }
    } else {
      setFilters(prev => ({
        ...prev,
        [filterKey]: value,
      }));
    }
  };

  const handleApply = () => {
    onApply?.(filters);
    onClose?.();
  };

  const handleClear = () => {
    const clearedFilters = {};
    filterConfig.forEach(f => {
      if (f.type === 'text') {
        clearedFilters[f.key] = '';
      } else if (f.type === 'radio') {
        clearedFilters[f.key] = '';
      } else {
        clearedFilters[f.key] = [];
      }
    });
    setFilters(clearedFilters);
    onClear?.();
  };

  return (
    <FilterModal
      open={open}
      onClose={onClose}
      title={title}
      pt = {0}
      desktopHeight = "60vh"
    >
      <Box sx={{ 
          display: 'flex', 
          flex: 1, 
          gap: 2, 
          overflow: 'hidden' ,
          position: 'relative',
          height: '100%',
          zIndex: 2
      }}>
        
          {/* Left Panel - Filter Categories */}
          <Box sx={{ 
              width: {xs: '140px', md: '200px'}, 
              borderRight: '1px solid #D9D9D9',
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2,
              pt: 3,
              pb: 2,
              overflowY: 'auto',
          }}>
              {filterConfig.map((filter) => (
              <Box 
                  key={filter.key} 
                  sx={{ 
                    cursor: 'pointer',
                    position: 'relative',
                    py: 0.5,
                  }}
                  onClick={() => setSelectedCategory(filter.key)}
              >
                  {selectedCategory === filter.key && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 3,
                        height: '100%',
                        backgroundColor: '#995A00',
                        borderRadius: '4px',
                      }}
                    />
                  )}
                  <Typography 
                      variant="labelD" 
                      sx={{
                          color: selectedCategory === filter.key ? '#995A00' : '#333',
                          display: 'flex',
                          alignItems: 'center',
                      }}
                  >
                  
                  {(filters[filter.key] && ((Array.isArray(filters[filter.key]) && filters[filter.key].length > 0) || (typeof filters[filter.key] === 'string' && filters[filter.key].trim() !== ''))) && (
                      <Box 
                          component="span" 
                              sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              backgroundColor: '#995A00', 
                              mr: 1 
                          }} 
                      />
                  )}
                  {filter.label}
                  </Typography>
              </Box>
              ))}
          </Box>

          {/* Right Panel - Filter Options - Scrollable */}
          <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2, 
              pt: 3, 
              pb: 2,
              flex: 1,
              overflowY: 'auto',
          }}>
              {selectedCategory && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1}}>
                  
                  {(() => {
                    const currentFilter = filterConfig.find(f => f.key === selectedCategory);
                    
                    if (currentFilter?.type === 'text') {
                      return (
                        <TextField
                          fullWidth
                          placeholder={'Type here'}
                          value={filters[selectedCategory] || ''}
                          onChange={(e) => handleTextChange(selectedCategory, e.target.value)}
                          size="small"
                          sx={{ mt: 1 }}
                          label={currentFilter?.label?.toLowerCase().includes('phone') ? 'Phone' : currentFilter?.label}
                          startAdornment={<IconReusable name='SearchOutlined' />}
                        />
                      );
                    }
                    
                    if (currentFilter?.type === 'radio') {
                      return (
                        <>
                          <Typography variant="labelD" sx={{ fontWeight: 600, color: '#333' }}>
                            Filter by {currentFilter?.label}
                          </Typography>
                          <CustomRadioGroup
                            value={filters[selectedCategory] || ''}
                            onChange={(e) => handleRadioChange(selectedCategory, e.target.value)}
                            options={currentFilter.options?.map(opt => ({
                              value: opt.key,
                              label: opt.label
                            })) || []}
                          />
                        </>
                      );
                    }
                    
                    if (currentFilter?.type === 'checkbox') {
                      return (
                        <>
                          <Typography variant="labelD" sx={{ fontWeight: 600, color: '#333' }}>
                            Filter by {currentFilter?.label}
                          </Typography>
                          <FormGroup sx={{ pl: 2 }}>
                            {currentFilter.options?.map((opt) => (
                              <FormControlLabel
                                key={opt.key}
                                control={
                                  <CustomCheckbox
                                    checked={filters[selectedCategory]?.includes(opt.key) || false}
                                    onChange={() => handleCheckboxChange(selectedCategory, opt.key)}
                                  />
                                }
                                label={
                                  <Typography variant="body1" sx={{ fontSize: '14px' }}>
                                    {opt.label}
                                  </Typography>
                                }
                              />
                            ))}
                          </FormGroup>
                        </>
                      );
                    }
                    
                    return null;
                  })()}
              </Box>
              )}

              {!selectedCategory && (
                  <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '300px',
                      color: '#666'
                  }}>
                      <Typography variant="body1">
                      Select a filter category from the left to see options
                      </Typography>
                  </Box>
              )}
          </Box>

          {/* Bottom Buttons */}
          <Box sx={{ 
              display: 'flex',
              py: 2,
              gap: 2,
              width: '100%',
              backgroundColor: '#fff',
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              px: 2,
              justifyContent: 'space-between',
              zIndex: 1000,
              borderRadius: 2,
              borderTop: {md: '1px solid #D9D9D9'},
          }}>
              <CustomButton
                variant="text"
                color="primary"
                label="Clear Filters"
                // disabled={activeFilterCount === 0}
                onClick={handleClear}
                sx={{ width: '50%' }}
              />
              <CustomButton
                variant="filled"
                color="primary"
                label="Apply"
                onClick={handleApply}
                sx={{ width: '50%' }}
              />
          </Box>
      </Box>
    </FilterModal>
  );
};
export const FilterButton = ({ onOpenFilter, activeFilterCount = 0, label = "Filter", size, variant, color }) => {
  return (
    <CustomButton
      variant={variant || "text"}
      color={color || 'primary'}

      endIcon={<FilterListIcon />}
      onClick={onOpenFilter}
      label={label}
      sx={{ 
        textTransform: 'none',
        position: 'relative'
      }}
      size={size}
    >
      {activeFilterCount > 0 && (
        <Chip
          label={activeFilterCount}
          size="small"
          color="primary"
          sx={{
            // height: 18,
            fontSize: {xs: '13px', md: '16px'},
            ml: 1
          }}
        />
      )}
    </CustomButton>
  );
};

// Filter Display Components for showing selected filters outside
export const FilterResultsCount = ({ 
  totalItems = 0,
  filteredItems = 0,
  currentPage = 1,
  itemsPerPage = 10,
  showPagination = false
}) => {
  const startItem = filteredItems > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, filteredItems);
  
  const getResultText = () => {
    if (filteredItems === 0) {
      return "No items found";
    }
    
    if (showPagination && filteredItems > itemsPerPage) {
      return `Showing ${startItem}-${endItem} of ${filteredItems} items`;
    }
    
    if (filteredItems !== totalItems) {
      return `Showing ${filteredItems} of ${totalItems} items`;
    }
    
    return `Showing ${filteredItems} items`;
  };

  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      {getResultText()}
    </Typography>
  );
};

export const SelectedFiltersDisplay = ({ 
  appliedFilters = {},
  filterConfig = [],
  onRemoveFilter,
  onClearAll,
  showClearAll = true
}) => {
  const getActiveFiltersChips = () => {
    const chips = [];
    
    filterConfig.forEach(filter => {
      const selectedOptions = appliedFilters[filter.key] || [];
      
      // Handle text filters
      if (filter.type === 'text' && typeof selectedOptions === 'string' && selectedOptions.trim() !== '') {
        chips.push(
          <CustomChip
            key={`${filter.key}-text`}
            label={`${selectedOptions}`}
            onDelete={() => onRemoveFilter?.(filter.key)}
            variant="filled"
          />
        );
      }
      // Handle radio filters
      else if (filter.type === 'radio' && typeof selectedOptions === 'string' && selectedOptions.trim() !== '') {
        const option = filter.options.find(opt => opt.key === selectedOptions);
        if (option) {
          chips.push(
            <CustomChip
              key={`${filter.key}-${selectedOptions}`}
              label={`${option.label}`}
              onDelete={() => onRemoveFilter?.(filter.key)}
              variant="filled"
            />
          );
        }
      }
      // Handle checkbox filters
      else if (filter.type === 'checkbox' && Array.isArray(selectedOptions)) {
        selectedOptions.forEach(optionKey => {
          const option = filter.options.find(opt => opt.key === optionKey);
          if (option) {
            chips.push(
              <CustomChip
                key={`${filter.key}-${optionKey}`}
                label={`${option.label}`}
                onDelete={() => onRemoveFilter?.(filter.key, optionKey)}
                variant="filled"
              />
            );
          }
        });
      }
    });

    return chips;
  };

  const activeChips = getActiveFiltersChips();

  if (activeChips.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      {showClearAll && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Active Filters ({activeChips.length})
          </Typography>
          
            <IconButton 
              onClick={onClearAll}
              size="small"
              sx={{ color: 'text.secondary' }}
              title="Clear all filters"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
        </Box>
      )}
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {activeChips}
      </Stack>
    </Box>
  );
};

export default DynamicFilter;
